import React, { FC, useEffect } from "react";
import { useQuery } from "react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { useResponsive } from "ahooks";
import { AxiosError } from "axios";

import { getCatalogCourse, getCatalogCourseUnits, getCategories } from "@api";
import { handleCourseErrors } from "@errors";

import { useIsExternalCatalog } from "@hooks";
import { getFetchingStatus } from "@utils/helpers";

import localStorageKeys from "@constants/localStorageKeys";
import queryKeys from "@constants/queryKeys";
import { URLS } from "@constants/urls";

import { CourseContent, CourseHeader, CourseOverview, Page, Skeletons } from "@components";
import MobileCourseContent from "@components/CourseOverview/components/CourseContent/MobileCourseContent";
import CourseHeaderMobile from "@components/CourseOverview/components/CourseHeader/CourseHeaderMobile";

const CatalogCourse: FC = () => {
  const { courseId } = useParams() as { courseId: string };
  const navigate = useNavigate();
  const isExternalView = useIsExternalCatalog();
  const { state } = useLocation();
  const { sm } = useResponsive();

  const {
    status: courseStatus,
    data: course,
    error: courseError,
  } = useQuery([queryKeys.catalogCourse, courseId], () => getCatalogCourse(courseId), {
    select: (course) => course._data,
    onError: (error: AxiosError) => {
      const handleError = (): void => navigate(URLS.catalog.index);
      const errorId = error?.response?.data._errors[0]?.id;
      const showNotification = errorId === "forbidden.course_not_available";

      handleCourseErrors(error, showNotification, handleError);
    },
    retry: (failureCount, error: AxiosError) => {
      if (error.response?.status) return failureCount < 3 && error.response?.status >= 500;
      return false;
    },
  });

  const {
    status: sectionsStatus,
    data: sections = [],
    error: sectionsError,
  } = useQuery([queryKeys.units, courseId], () => getCatalogCourseUnits(courseId.toString()), {
    select: (sections) => sections._data,
  });

  const { data: categoryTree = [] } = useQuery(
    [queryKeys.categories.categories],
    () => getCategories(),
    {
      select: (res) => res._data,
    },
  );

  const status = getFetchingStatus([courseStatus, sectionsStatus]);
  const error = courseError || sectionsError;
  const nonCmsBackLink = URLS.catalog.index;
  const backLink = state && state.isCustomHomePage ? state.pathname : nonCmsBackLink;

  // On component unmount
  useEffect(() => {
    return () => {
      localStorage.removeItem(localStorageKeys.PAYMENT_STATUS);
      localStorage.removeItem(localStorageKeys.EXTERNAL_SIGNIN_SIGNUP);
    };
  }, []);

  return (
    <Page pageTitle={course?.name ?? ""}>
      <Skeletons.Loader status={status} error={error} fullScreen>
        {course && (
          <CourseOverview>
            <CourseOverview.Header isStickyDisabled={isExternalView || !sm} hideHeaderStyles={!sm}>
              {!sm ? (
                <CourseHeaderMobile
                  course={course}
                  categoryTree={categoryTree}
                  sections={sections}
                  backLink={backLink}
                />
              ) : (
                <CourseHeader
                  course={course}
                  categoryTree={categoryTree}
                  sections={sections}
                  backLink={backLink}
                  isCatalogCourseView
                />
              )}
            </CourseOverview.Header>
            <CourseOverview.Content>
              {!sm ? (
                <MobileCourseContent
                  course={course}
                  sections={sections}
                  categoryTree={categoryTree}
                  isCatalogCourseView
                />
              ) : (
                <CourseContent course={course} sections={sections} isCatalogCourseView />
              )}
            </CourseOverview.Content>
          </CourseOverview>
        )}
      </Skeletons.Loader>
    </Page>
  );
};

export default CatalogCourse;
